/**
 * 경기 시별 지도 path 데이터.
 *
 * 디자이너가 시별로 분리해서 export한 SVG들을 318×402 캔버스 안에 절대 좌표로 배치.
 * 좌표는 Figma 인스턴스(`17690:39949`)의 메타데이터에서 SVG width/height 사이즈 매칭으로 추출.
 *
 * 데이터 추가/수정 흐름:
 *  1. `apps/service-area/src/assets/maps/경기/<시이름>.svg` 추가 (디자이너 export)
 *  2. 본 파일에 import + GYEONGGI_REGIONS 배열에 항목 추가
 *  3. width/height는 SVG 파일 첫 줄 viewBox에서 가져옴
 *  4. x/y는 Figma 디자인 기준 절대 좌표
 *  5. label 좌표는 path 옆/위/아래 중 디자인 의도에 맞게 (작은 시는 영역 외부)
 *
 * 새 시도(예: 인천) 추가 시 같은 패턴으로 `incheonRegions.ts` 별도 작성.
 */

import goyangSvgUrl from '@/assets/maps/경기/고양.svg'
import gwangmyeongSvgUrl from '@/assets/maps/경기/광명.svg'
import guriSvgUrl from '@/assets/maps/경기/구리.svg'
import gunpoSvgUrl from '@/assets/maps/경기/군포.svg'
import gimpoSvgUrl from '@/assets/maps/경기/김포.svg'
import namyangjuSvgUrl from '@/assets/maps/경기/남양주.svg'
import bucheonSvgUrl from '@/assets/maps/경기/부천.svg'
import seongnamSvgUrl from '@/assets/maps/경기/성남.svg'
import suwonSvgUrl from '@/assets/maps/경기/수원.svg'
import siheungSvgUrl from '@/assets/maps/경기/시흥.svg'
import ansanSvgUrl from '@/assets/maps/경기/안산.svg'
import anyangSvgUrl from '@/assets/maps/경기/안양.svg'
import yangjuSvgUrl from '@/assets/maps/경기/양주.svg'
import yonginSvgUrl from '@/assets/maps/경기/용인.svg'
import uijeongbuSvgUrl from '@/assets/maps/경기/의정부.svg'
import pajuSvgUrl from '@/assets/maps/경기/파주.svg'
import hanamSvgUrl from '@/assets/maps/경기/하남.svg'

import type { RegionCode } from '../types/region'

/**
 * 필터별 캔버스 크기 — 디자이너가 필터별로 별도 인스턴스 그림.
 * 같은 시 SVG를 재사용하되 좌표/라벨이 필터마다 다름.
 *  - full: 17개 시 합성 (기본 화면)
 *  - north: 북부 7개 시 (파주/양주/의정부/고양/남양주/구리/김포)
 *  - south: 남부 10개 시 (하남/부천/시흥/광명/안산/안양/성남/수원/군포/용인)
 */
export const GYEONGGI_CANVAS_FULL = { width: 318, height: 402 } as const
export const GYEONGGI_CANVAS_NORTH = { width: 318, height: 223 } as const
export const GYEONGGI_CANVAS_SOUTH = { width: 318, height: 275 } as const

/** 하위 호환 — GyeonggiMap이 기본(전체) 캔버스 import할 때 */
export const GYEONGGI_CANVAS = GYEONGGI_CANVAS_FULL

/**
 * 화면 노출 시 비례 축소 배율. 좌표(% 기반)는 그대로 두고 max-width 만 곱한다.
 *
 * 이전: 237×300 (Figma 18338:215301 기준, scale ≈ 0.745) — 부천/광명/안양 등 작은 시 라벨이
 *       너무 작아 의도와 다른 시군구로 스크롤되는 QA 피드백 발생.
 * 변경: 285×360 (scale ≈ 0.896) — 캔버스 비율(318/402) 유지하면서 height 360px 로 확대.
 *       작은 시의 hit area 가 시각적으로 충분히 커짐.
 */
export const GYEONGGI_RENDER_SCALE = 360 / 402

/** 경기 필터 종류 */
export type GyeonggiFilter = 'full' | 'north' | 'south'

export interface RegionPathData {
  /** 표준 행정구역 코드 (5자리 시군구) — scrollToSiGunGu 매핑 키 */
  code: RegionCode
  /** 시 이름 (라벨로 표시) */
  name: string
  /** SVG asset URL (정적 import) */
  svgUrl: string
  /** SVG 자체 viewBox 너비 */
  width: number
  /** SVG 자체 viewBox 높이 */
  height: number
  /** 캔버스 기준 SVG 좌상단 x 좌표 (px) */
  x: number
  /** 캔버스 기준 y 좌표 (px) */
  y: number
  /**
   * 라벨 위치 (캔버스 기준 절대 좌표).
   * 라벨은 텍스트 중앙(기준점)이 (x, y)에 위치하도록 렌더.
   * - 큰 시 (수원/성남/고양 등): path 영역 안 중앙
   * - 작은 시 (광명/구리/안양/군포 등): path 영역 밖 — 화살표/라인 없이 텍스트만
   */
  label: { x: number; y: number }
}

/**
 * 경기 전체 (full) — 17개 시. 캔버스 318×402.
 *
 * x/y/width/height는 Figma 좌표. 라벨 좌표는 path 중심 또는 외부 (작은 시는 외부).
 * 디자이너가 필터(전체/북부/남부)별로 좌표를 다르게 그렸으니
 * 다른 필터는 GYEONGGI_REGIONS_NORTH / GYEONGGI_REGIONS_SOUTH 참조.
 */
export const GYEONGGI_REGIONS_FULL: readonly RegionPathData[] = [
  // 북부
  { code: '41480', name: '파주', svgUrl: pajuSvgUrl, width: 123, height: 137, x: 51, y: 1, label: { x: 110, y: 78 } },
  {
    code: '41630',
    name: '양주',
    svgUrl: yangjuSvgUrl,
    width: 77,
    height: 117,
    x: 136,
    y: 30,
    label: { x: 174, y: 78 },
  },
  {
    code: '41150',
    name: '의정부',
    svgUrl: uijeongbuSvgUrl,
    width: 51,
    height: 42,
    x: 169,
    y: 98,
    label: { x: 194, y: 119 },
  },
  {
    code: '41280',
    name: '고양',
    svgUrl: goyangSvgUrl,
    width: 112,
    height: 76,
    x: 56,
    y: 112,
    label: { x: 110, y: 148 },
  },
  {
    code: '41360',
    name: '남양주',
    svgUrl: namyangjuSvgUrl,
    width: 104,
    height: 110,
    x: 196,
    y: 100,
    label: { x: 248, y: 148 },
  },
  { code: '41310', name: '구리', svgUrl: guriSvgUrl, width: 27, height: 40, x: 202, y: 157, label: { x: 215, y: 178 } },
  // 서부
  { code: '41570', name: '김포', svgUrl: gimpoSvgUrl, width: 99, height: 88, x: 2, y: 95, label: { x: 35, y: 138 } },
  {
    code: '41190',
    name: '부천',
    svgUrl: bucheonSvgUrl,
    width: 32,
    height: 40,
    x: 79,
    y: 198,
    label: { x: 95, y: 218 },
  },
  {
    code: '41390',
    name: '시흥',
    svgUrl: siheungSvgUrl,
    width: 67,
    height: 68,
    x: 61,
    y: 233,
    label: { x: 95, y: 265 },
  },
  { code: '41270', name: '안산', svgUrl: ansanSvgUrl, width: 73, height: 47, x: 74, y: 274, label: { x: 112, y: 298 } },
  // 중부 — 광명/안양/군포는 path가 작고 세로 일렬이라 인접 라벨끼리 겹치기 쉬운 문제로
  // 광명은 path 영역 안, 안양/군포는 우측 외부로 충분히 떨어뜨려 분산
  {
    code: '41210',
    name: '광명',
    svgUrl: gwangmyeongSvgUrl,
    width: 26,
    height: 41,
    x: 109,
    y: 223,
    label: { x: 122, y: 243 },
  },
  {
    code: '41170',
    name: '안양',
    svgUrl: anyangSvgUrl,
    width: 28,
    height: 37,
    x: 124,
    y: 243,
    label: { x: 138, y: 262 },
  },
  {
    code: '41410',
    name: '군포',
    svgUrl: gunpoSvgUrl,
    width: 33,
    height: 35,
    x: 124,
    y: 273,
    label: { x: 140, y: 290 },
  },
  {
    code: '41110',
    name: '수원',
    svgUrl: suwonSvgUrl,
    width: 58,
    height: 55,
    x: 143,
    y: 285,
    label: { x: 172, y: 320 },
  },
  // 동부
  {
    code: '41450',
    name: '하남',
    svgUrl: hanamSvgUrl,
    width: 52,
    height: 53,
    x: 217,
    y: 180,
    label: { x: 243, y: 212 },
  },
  {
    code: '41130',
    name: '성남',
    svgUrl: seongnamSvgUrl,
    width: 59,
    height: 62,
    x: 178,
    y: 232,
    label: { x: 207, y: 263 },
  },
  {
    code: '41460',
    name: '용인',
    svgUrl: yonginSvgUrl,
    width: 111,
    height: 120,
    x: 207,
    y: 281,
    label: { x: 262, y: 341 },
  },
] as const

/** 하위 호환 별칭 — 외부에서 이 이름을 import해서 쓰는 곳을 위해 유지 */
export const GYEONGGI_REGIONS = GYEONGGI_REGIONS_FULL

/**
 * 경기 북부 (north) — 7개 시. 캔버스 318×223.
 * Figma frame 17690:39998에서 추출한 좌표.
 *
 * 우리 SVG width/height는 디자이너 표시 사이즈와 약간 다름 (디자이너가 키워 표시).
 * width만 디자인 사이즈로 강제 → height는 img 자체 viewBox 비율로 자동 (preserveAspectRatio 기본).
 * 결과: path 비율 유지하며 우리 viewBox 기준 height가 자동 결정 (디자인보다 약간 작을 수 있음).
 */
export const GYEONGGI_REGIONS_NORTH: readonly RegionPathData[] = [
  { code: '41480', name: '파주', svgUrl: pajuSvgUrl, width: 129, height: 143, x: 54, y: 2, label: { x: 110, y: 88 } },
  {
    code: '41630',
    name: '양주',
    svgUrl: yangjuSvgUrl,
    width: 80,
    height: 123,
    x: 144,
    y: 32,
    label: { x: 180, y: 79 },
  },
  {
    code: '41150',
    name: '의정부',
    svgUrl: uijeongbuSvgUrl,
    width: 54,
    height: 44,
    x: 178,
    y: 102,
    label: { x: 205, y: 123 },
  },
  {
    code: '41280',
    name: '고양',
    svgUrl: goyangSvgUrl,
    width: 116,
    height: 79,
    x: 60,
    y: 118,
    label: { x: 114, y: 155 },
  },
  {
    code: '41360',
    name: '남양주',
    svgUrl: namyangjuSvgUrl,
    width: 110,
    height: 116,
    x: 207,
    y: 104,
    label: { x: 265, y: 153 },
  },
  { code: '41310', name: '구리', svgUrl: guriSvgUrl, width: 28, height: 42, x: 212, y: 164, label: { x: 225, y: 184 } },
  { code: '41570', name: '김포', svgUrl: gimpoSvgUrl, width: 103, height: 93, x: 4, y: 100, label: { x: 35, y: 145 } },
] as const

/**
 * 경기 남부 (south) — 10개 시. 캔버스 318×275.
 * Figma frame 17724:42237에서 추출한 좌표.
 */
export const GYEONGGI_REGIONS_SOUTH: readonly RegionPathData[] = [
  { code: '41450', name: '하남', svgUrl: hanamSvgUrl, width: 63, height: 64, x: 193, y: 4, label: { x: 220, y: 40 } },
  { code: '41190', name: '부천', svgUrl: bucheonSvgUrl, width: 39, height: 49, x: 24, y: 24, label: { x: 44, y: 46 } },
  { code: '41390', name: '시흥', svgUrl: siheungSvgUrl, width: 82, height: 84, x: 1, y: 66, label: { x: 50, y: 105 } },
  {
    code: '41210',
    name: '광명',
    svgUrl: gwangmyeongSvgUrl,
    width: 31,
    height: 49,
    x: 60,
    y: 56,
    label: { x: 75, y: 81 },
  },
  { code: '41270', name: '안산', svgUrl: ansanSvgUrl, width: 90, height: 57, x: 17, y: 116, label: { x: 59, y: 145 } },
  { code: '41170', name: '안양', svgUrl: anyangSvgUrl, width: 34, height: 47, x: 77, y: 79, label: { x: 92, y: 103 } },
  {
    code: '41130',
    name: '성남',
    svgUrl: seongnamSvgUrl,
    width: 72,
    height: 75,
    x: 146,
    y: 66,
    label: { x: 180, y: 105 },
  },
  {
    code: '41110',
    name: '수원',
    svgUrl: suwonSvgUrl,
    width: 70,
    height: 68,
    x: 102,
    y: 131,
    label: { x: 135, y: 165 },
  },
  { code: '41410', name: '군포', svgUrl: gunpoSvgUrl, width: 40, height: 42, x: 78, y: 115, label: { x: 100, y: 134 } },
  {
    code: '41460',
    name: '용인',
    svgUrl: yonginSvgUrl,
    width: 135,
    height: 147,
    x: 181,
    y: 126,
    label: { x: 235, y: 200 },
  },
] as const

/** 필터 → 캔버스 + 시 데이터 매핑. GyeonggiMap이 분기 시 사용 */
export const GYEONGGI_BY_FILTER = {
  full: { canvas: GYEONGGI_CANVAS_FULL, regions: GYEONGGI_REGIONS_FULL },
  north: { canvas: GYEONGGI_CANVAS_NORTH, regions: GYEONGGI_REGIONS_NORTH },
  south: { canvas: GYEONGGI_CANVAS_SOUTH, regions: GYEONGGI_REGIONS_SOUTH },
} as const satisfies Record<
  GyeonggiFilter,
  { canvas: { width: number; height: number }; regions: readonly RegionPathData[] }
>

/**
 * 지도 region.code (행정구역 5자리) → 백엔드 SiGunGu.name (한글) 매핑.
 *
 * 라벨에 표시되는 이름("수원")은 짧지만, 백엔드/스크롤 매칭용 키는 시군구 정식명("수원시").
 * 지도 라벨 클릭 시 `scrollToSiGunGu`가 본 매핑으로 시군구 카드 id를 찾아 스크롤.
 */
export const GYEONGGI_CODE_TO_SIGUNGU_NAME: Record<string, string> = {
  '41110': '수원시',
  '41130': '성남시',
  '41150': '의정부시',
  '41170': '안양시',
  '41190': '부천시',
  '41210': '광명시',
  '41270': '안산시',
  '41280': '고양시',
  '41310': '구리시',
  '41360': '남양주시',
  '41390': '시흥시',
  '41410': '군포시',
  '41450': '하남시',
  '41460': '용인시',
  '41480': '파주시',
  '41570': '김포시',
  '41630': '양주시',
}
